import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Permission {
  id: string;
  key: string;
  description: string | null;
}

interface RolePermission {
  role: string;
  permission_key: string;
}

interface ACLState {
  permissions: Permission[];
  rolePermissions: RolePermission[];
  loading: boolean;
  loadPermissions: () => Promise<void>;
  loadRolePermissions: () => Promise<void>;
  toggleRolePermission: (role: string, permissionKey: string, enabled: boolean) => Promise<void>;
}

export const useACLStore = create<ACLState>((set, get) => ({
  permissions: [],
  rolePermissions: [],
  loading: false,

  loadPermissions: async () => {
    set({ loading: true });
    try {
      const { data, error } = await (supabase as any)
        .from('permissions')
        .select('*')
        .order('key');

      if (error) throw error;
      set({ permissions: data || [] });
    } catch (error) {
      console.error('Failed to load permissions:', error);
      toast.error('Failed to load permissions');
    } finally {
      set({ loading: false });
    }
  },

  loadRolePermissions: async () => {
    set({ loading: true });
    try {
      const { data, error } = await (supabase as any)
        .from('role_permissions')
        .select('*');

      if (error) throw error;
      set({ rolePermissions: data || [] });
    } catch (error) {
      console.error('Failed to load role permissions:', error);
      toast.error('Failed to load role permissions');
    } finally {
      set({ loading: false });
    }
  },

  toggleRolePermission: async (role: string, permissionKey: string, enabled: boolean) => {
    try {
      if (enabled) {
        // Insert permission
        const { error } = await (supabase as any)
          .from('role_permissions')
          .insert({ role, permission_key: permissionKey });

        if (error) throw error;
        toast.success(`Permission granted to ${role}`);
      } else {
        // Delete permission
        const { error } = await (supabase as any)
          .from('role_permissions')
          .delete()
          .eq('role', role)
          .eq('permission_key', permissionKey);

        if (error) throw error;
        toast.success(`Permission revoked from ${role}`);
      }

      // Reload
      await get().loadRolePermissions();
    } catch (error) {
      console.error('Failed to toggle role permission:', error);
      toast.error('Failed to update permission');
    }
  },
}));
